"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Card } from "./ui/card";
import { AstroMascot } from "./AstroMascot";
import { 
  FileText, 
  CheckCircle, 
  Upload, 
  Pen, 
  Download,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  X,
  ArrowLeft,
  Briefcase,
  Sparkles
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface AppointmentLetterProps {
  onComplete?: () => void;
  onShowTerms?: () => void;
  onBack?: () => void;
}

type SigningStep = "review" | "signing" | "success" | "error";

export function AppointmentLetter({ onComplete, onShowTerms, onBack }: AppointmentLetterProps) {
  const [currentStep, setCurrentStep] = React.useState<SigningStep>("review");
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(false);
  const [signatureType, setSignatureType] = React.useState<"draw" | "upload" | null>(null);
  const [signatureFile, setSignatureFile] = React.useState<File | null>(null);
  const [drawnSignature, setDrawnSignature] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // PDF content simulation
  const pdfContent = `
APPOINTMENT LETTER

OpenTurf Technologies Pvt. Ltd.
Bangalore, Karnataka, India

Date: ${new Date().toLocaleDateString('en-IN')}

Dear [Candidate Name],

We are pleased to offer you the position of [Job Title] at OpenTurf Technologies Pvt. Ltd.

TERMS AND CONDITIONS:

1. Position: You will be employed as [Job Title] in our [Department] department.

2. Start Date: Your employment will commence on [Start Date].

3. Compensation: Your annual salary will be INR [Amount] per annum, payable monthly.

4. Working Hours: Standard working hours are 9:00 AM to 6:00 PM, Monday through Friday.

5. Benefits: You will be entitled to health insurance, provident fund, and other benefits as per company policy.

6. Probation: You will be on probation for the first 6 months of employment.

7. Confidentiality: You agree to maintain confidentiality of all company information.

8. Termination: Either party may terminate employment with 30 days written notice.

Please sign and return this letter to confirm your acceptance of this offer.

We look forward to welcoming you to our team!

Sincerely,
HR Department
OpenTurf Technologies Pvt. Ltd.
  `;

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setDrawnSignature(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawnSignature(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSignatureFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!hasAcceptedTerms || (!drawnSignature && !signatureFile)) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    
    // Simulate success/failure (90% success rate)
    const isSuccess = Math.random() > 0.1;
    setCurrentStep(isSuccess ? "success" : "error");
    
    if (isSuccess) {
      setTimeout(() => {
        onComplete?.();
      }, 3000);
    }
  };

  const canSubmit = hasAcceptedTerms && (drawnSignature || signatureFile);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  if (currentStep === "success") {
    return <SuccessScreen onShowTerms={onShowTerms} />;
  }

  if (currentStep === "error") {
    return (
      <ErrorScreen 
        onRetry={() => setCurrentStep("review")}
        onShowTerms={onShowTerms}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-32 left-16 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl"
          animate={{ 
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-32 right-16 w-40 h-40 bg-indigo-200/20 rounded-full blur-3xl"
          animate={{ 
            y: [0, 20, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <div className="h-6 w-px bg-gray-300" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Briefcase className="w-5 h-5 text-indigo-600" />
              <span>Appointment Letter Signing</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Header */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <h1 className="text-gray-900 mb-4">
                  Review &amp; Sign Your Appointment Letter 📝
                </h1>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Take a moment to carefully review your employment terms. Once you're ready, add your signature to make it official.
                </p>
              </motion.div>
            </div>

            {/* Astro Guide */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200 p-6">
                <div className="flex items-start space-x-6">
                  <motion.div
                    animate={{ 
                      y: [0, -5, 0],
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <AstroMascot expression="proud" size="lg" />
                  </motion.div>
                  
                  <div className="flex-1">
                    <div className="bg-white rounded-2xl p-6 border border-indigo-200 relative">
                      {/* Speech bubble pointer */}
                      <div className="absolute left-0 top-6 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[12px] border-r-white -translate-x-3" />
                      
                      <p className="text-gray-800 leading-relaxed">
                        🎩 This is the big moment — let's make it official! I've put on my finest suit for the occasion. 
                        Review your appointment letter carefully, then add your signature to seal the deal. 
                        <span className="inline-flex items-center ml-2">
                          <Sparkles className="w-4 h-4 text-indigo-600" />
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Main Layout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* PDF Viewer */}
              <div className="lg:col-span-2">
                <Card className="h-[700px] bg-white/90 backdrop-blur-sm border border-gray-200 overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-gray-900 flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <span>Appointment Letter</span>
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-white rounded-xl p-8 shadow-inner border border-gray-100">
                      <pre className="whitespace-pre-wrap text-gray-800 leading-relaxed font-mono text-sm">
                        {pdfContent}
                      </pre>
                      
                      {/* Signature Area in Document */}
                      <div className="mt-12 pt-8 border-t border-gray-300">
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-gray-700 mb-4">Employee Signature:</p>
                            <div className="w-64 h-16 border-b-2 border-gray-400 relative">
                              {(drawnSignature || signatureFile) && (
                                <div className="absolute inset-0 flex items-end pb-2">
                                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                    ✓ Signature Added
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-2">Date: {new Date().toLocaleDateString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-gray-700 mb-4">HR Representative:</p>
                            <div className="w-64 h-16 border-b-2 border-gray-400 relative">
                              <div className="absolute inset-0 flex items-end pb-2">
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                  ✓ Pre-signed
                                </span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">Date: {new Date().toLocaleDateString('en-IN')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Signing Panel */}
              <div className="lg:col-span-1">
                <Card className="h-[700px] bg-white/90 backdrop-blur-sm border border-gray-200 p-6 flex flex-col overflow-y-auto">
                  <h3 className="text-gray-900 mb-6 flex items-center space-x-2">
                    <Pen className="w-5 h-5 text-indigo-600" />
                    <span>Add Your Signature</span>
                  </h3>
                  
                  {/* Terms Confirmation */}
                  <div className="mb-6">
                    <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                      <div className="flex items-start space-x-3">
                        <Checkbox 
                          id="terms"
                          checked={hasAcceptedTerms}
                          onCheckedChange={(checked) => setHasAcceptedTerms(!!checked)}
                          className="mt-0.5"
                        />
                        <label 
                          htmlFor="terms" 
                          className="text-sm text-blue-800 leading-relaxed cursor-pointer"
                        >
                          I've read and agree to the terms and conditions outlined in this appointment letter.
                        </label>
                      </div>
                    </Card>
                  </div>

                  {/* Signature Options */}
                  <div className="flex-1 space-y-4">
                    <h4 className="text-gray-900">Choose Signature Method</h4>
                    
                    {/* Signature Type Selection */}
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        variant={signatureType === "draw" ? "default" : "outline"}
                        onClick={() => setSignatureType("draw")}
                        className="h-auto p-4 flex-col space-y-2"
                      >
                        <Pen className="w-5 h-5" />
                        <span className="text-xs">Draw Signature</span>
                      </Button>
                      
                      <Button
                        variant={signatureType === "upload" ? "default" : "outline"}
                        onClick={() => setSignatureType("upload")}
                        className="h-auto p-4 flex-col space-y-2"
                      >
                        <Upload className="w-5 h-5" />
                        <span className="text-xs">Upload File</span>
                      </Button>
                    </div>

                    {/* Draw Signature */}
                    {signatureType === "draw" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3"
                      >
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 bg-white">
                          <canvas
                            ref={canvasRef}
                            width={240}
                            height={120}
                            className="w-full h-30 border rounded-lg bg-gray-50 cursor-crosshair"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                          />
                        </div>
                        <p className="text-xs text-gray-500">Sign above using your mouse or touch screen</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSignature}
                          className="w-full"
                        >
                          Clear Signature
                        </Button>
                      </motion.div>
                    )}

                    {/* Upload Signature */}
                    {signatureType === "upload" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3"
                      >
                        <div
                          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-gray-400 transition-colors bg-white"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {signatureFile ? (
                            <div className="space-y-2">
                              <CheckCircle className="w-8 h-8 text-green-600 mx-auto" />
                              <p className="text-sm text-gray-700">{signatureFile.name}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSignatureFile(null);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600">
                                Click to upload signature image
                              </p>
                              <p className="text-xs text-gray-500">
                                PNG, JPG up to 2MB
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </motion.div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6 border-t border-gray-200 space-y-4">
                    <Button
                      onClick={handleSubmit}
                      disabled={!canSubmit || isSubmitting}
                      className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed py-3 relative overflow-hidden"
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 mr-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </motion.div>
                          Submitting Signed Letter...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Submit Signed Letter
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-md"
                            animate={{ x: ["-100%", "100%"] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          />
                        </>
                      )}
                    </Button>
                    
                    {!canSubmit && (
                      <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs text-gray-500 text-center"
                      >
                        Please accept terms and add your signature to continue
                      </motion.p>
                    )}
                  </div>
                </Card>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Success Screen Component
function SuccessScreen({ onShowTerms }: { onShowTerms?: () => void }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-green-200/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.6, 0.3, 0.6] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 text-center max-w-lg mx-auto"
        >
          {/* Success Icon with Celebration */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto flex items-center justify-center mb-8 relative"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, duration: 0.3 }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            {/* Celebration particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                initial={{ scale: 0, x: 0, y: 0 }}
                animate={{ 
                  scale: [0, 1, 0],
                  x: [0, (Math.random() - 0.5) * 100],
                  y: [0, (Math.random() - 0.5) * 100]
                }}
                transition={{ 
                  delay: 1 + i * 0.1,
                  duration: 1.5,
                  ease: "easeOut"
                }}
              />
            ))}
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-gray-900 mb-4"
          >
            Congratulations! Letter Signed Successfully! 🎉
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-gray-600 mb-8"
          >
            Your appointment letter has been officially signed and submitted. Welcome to the OpenTurf family!
          </motion.p>

          {/* Success Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50 mb-8"
          >
            <h3 className="text-gray-900 mb-4">What Happens Next?</h3>
            <div className="text-left space-y-3 text-sm text-gray-600">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>You'll receive a welcome email with first-day instructions within 24 hours</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>HR will contact you with joining details and office access information</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Keep an eye out for your employee handbook and system credentials</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Astro Celebration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.6 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200"
          >
            <div className="flex items-center justify-center space-x-4">
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <AstroMascot expression="excited" size="md" />
              </motion.div>
              <div className="text-left">
                <p className="text-green-800 text-sm">
                  🎊 Fantastic! You're officially part of the crew now. 
                  Can't wait to see the amazing things you'll accomplish here!
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-green-600 hover:text-green-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Error Screen Component
function ErrorScreen({ onRetry, onShowTerms }: { onRetry: () => void; onShowTerms?: () => void }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-red-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full mx-auto flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-gray-900 mb-4">
            Submission Failed 😔
          </h1>
          
          <p className="text-gray-600 mb-8">
            We couldn't process your signed letter right now. Please try submitting again.
          </p>

          <div className="space-y-4">
            <Button
              onClick={onRetry}
              className="bg-indigo-600 hover:bg-indigo-700 text-white w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}